// A counter display's screen changes: the entrance state, the flip clock, and the
// sample loop the editor and the Designs page play. The runtime uses the same hook.
import { useEffect, useRef, useState } from 'react'
import { STILL, motionAfter, type Flash } from '../lib/counterDisplay'
import { FLIP_MS, screenChangeOf, useReducedMotion, type FlipFrame, type ScreenFlip } from './counterMotion'
import { LayoutSurface, type LayoutBindings } from './render'
import type { CounterScreen, LayoutDocument, ScreenChange } from './types'

/** The data-* a .counter-shell carries for its entrance, recall and ring. */
export interface ShellMotion {
  'data-enter': string
  'data-recall'?: string
  'data-ring'?: string
}

/**
 * The screen change for what is shown now. A new entrance key changes screen (a new
 * call included), a recall replays the number. Under the flip clock, the last frame
 * drawn is kept and folded away; reduced motion fades instead.
 */
export function useCounterStage(
  change: ScreenChange,
  screen: CounterScreen,
  enter: string,
  flash: Flash | null,
  bindings: LayoutBindings,
): { shell: ShellMotion; bindings: LayoutBindings } {
  const reduced = useReducedMotion()
  const [motion, setMotion] = useState(STILL)
  const [flip, setFlip] = useState<ScreenFlip | null>(null)
  const last = useRef<FlipFrame | null>(null)
  const flips = change === 'flip' && !reduced

  const next = motionAfter(motion, enter, flash)
  if (next !== motion) {
    const entered = next.enter !== motion.enter
    const recalled = !entered && next.recalled !== '' && next.recalled !== motion.recalled
    if (flips && last.current && (entered || recalled)) {
      setFlip({ serial: (flip?.serial ?? 0) + 1, from: last.current, numberOnly: !entered })
    }
    setMotion(next)
  }

  useEffect(() => {
    last.current = { screen, bindings }
  })

  useEffect(() => {
    if (!flip) return
    const timer = window.setTimeout(() => setFlip((f) => (f === flip ? null : f)), FLIP_MS)
    return () => window.clearTimeout(timer)
  }, [flip])

  return {
    shell: { 'data-enter': motion.entered, 'data-recall': motion.recalled || undefined, 'data-ring': motion.ringed || undefined },
    bindings: { ...bindings, reducedMotion: reduced, flip: flips ? (flip ?? undefined) : undefined },
  }
}

/** A sample call's number: A-042 (the sample's own), A-043, … */
export function sampleNumber(call: number): string {
  return `A-${String(42 + (call % 958)).padStart(3, '0')}`
}

/** Steps a sample desk on a timer while on: 0 is Called, then Free, Called, … */
export function useLoopStep(on: boolean, firstMs: number, everyMs: number): number {
  const [step, setStep] = useState(0)
  useEffect(() => {
    if (!on) return
    let timer = window.setTimeout(function tick() {
      setStep((s) => s + 1)
      timer = window.setTimeout(tick, everyMs)
    }, firstMs)
    return () => window.clearTimeout(timer)
  }, [on, firstMs, everyMs])
  return step
}

/**
 * A counter design's thumbnail: still, or while `playing`, its desk going Free and
 * Called with a new number each call. Still under reduced motion.
 */
export function CounterLoop({ document: doc, bindings, playing }: { document: LayoutDocument; bindings: LayoutBindings; playing: boolean }) {
  const reduced = useReducedMotion()
  return !playing || reduced ? (
    <LayoutSurface document={doc} screen="called" bindings={bindings} />
  ) : (
    <PlayingLoop document={doc} bindings={bindings} />
  )
}

function PlayingLoop({ document: doc, bindings }: { document: LayoutDocument; bindings: LayoutBindings }) {
  const step = useLoopStep(true, 350, 2200)
  const screen: CounterScreen = step % 2 === 0 ? 'called' : 'free'
  const call = Math.floor(step / 2)
  const flash = screen === 'called' && step > 0 ? { key: String(call), until: 0, recall: false } : null
  const stage = useCounterStage(screenChangeOf(doc.canvas), screen, screen === 'called' ? `called:${call}` : 'free', flash, {
    ...bindings,
    ticketNumber: screen === 'called' ? sampleNumber(call) : '',
    deskPill: screen === 'called' ? 'serving' : 'open',
  })
  return (
    <span className="counter-shell counter-loop" {...stage.shell}>
      <LayoutSurface document={doc} screen={screen} bindings={stage.bindings} />
    </span>
  )
}
